import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { ModalService } from 'src/utils/modal.service';
import { ToastService } from 'src/utils/toast.service';

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [CommonModule, NgOptimizedImage],
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.scss',
})
export class ModalComponent {
  @Input() imageUrl: string = '';
  @Input() title: string = '';
  @Input() message: string = '';
  @Input() cancelButtonText: string = 'Cancelar';
  @Input() continueButtonText: string = 'Continuar';
  @Input() showCancelButton: boolean = false;
  @Output() close = new EventEmitter<void>();

  get modalVariant(): 'default' | 'legacy' {
    return this.modalService.modalVariant();
  }

  modalService = inject(ModalService);
  toastService = inject(ToastService);

  modalVisible = this.modalService.modalVisible;

  onClose(triggerCancel = false) {
    // Si no hay triggerCancel explícito, tratarlo como cancelación
    // para asegurar que canDeactivate resuelva correctamente
    const shouldCancel = triggerCancel || this.modalService.onCancel !== null;
    this.modalService.closeModal(shouldCancel);
    this.close.emit();
  }

  onBackdropClose() {
    if (!this.modalService.isStatic()) this.onClose(true);
  }

  onConfirmClick() {
    if (this.modalService.onConfirm) {
      this.modalService.onConfirm();
    }
    
    if (this.modalService.closeOnConfirm()) {
      this.modalService.closeModal();
    }

    if (this.modalService.showToastOnConfirm()) {
      this.toastService.showToast(
        'fi fi-ss-check-circle text-[24px] text-success',
        'Estado actualizado de manera exitosa',
        'success'
      );
    }
  }

  getModalClasses(): string {
    if (this.modalVariant === 'legacy') {
      return 'bg-red-500 rounded-3xl shadow-custom pb-8 pt-12 px-8 w-[440px] max-w-full';
    } else {
      return 'bg-light rounded-3xl shadow-modal pb-6 pt-[40px] xs:pt-[48px] px-4 xs:px-6 w-[327px] xs:w-[440px] max-w-full';
    }
  }

  getButtonClasses(buttonType: 'primary' | 'secondary' | 'danger'): string {
    if (this.modalVariant === 'legacy') {
      const baseClasses =
        'rounded-2xl font-poppins font-semibold px-3 min-h-12 h-auto py-2 transition-all text-sm w-full xs:w-auto flex-auto xs:flex-1';

      switch (buttonType) {
        case 'primary':
          return `${baseClasses} bg-primary-g300 text-white hover:bg-primary-g400 active:bg-primary-g500`;
        case 'secondary':
          return `${baseClasses} bg-transparent text-secundary-b300 hover:bg-secundary-b50 hover:text-secundary-b400 active:bg-secundary-b100 active:text-secundary-b500`;
        case 'danger':
          return `${baseClasses} bg-error text-white hover:bg-red-600 active:bg-red-800`;
        default:
          return `${baseClasses} bg-primary-g300 text-white hover:bg-primary-g400`;
      }
    } else {
      const baseClasses =
        'rounded-2xl font-poppins font-medium px-[12px] xs:px-[16px] min-h-12 h-auto py-2 transition-all flex-1 text-base';

      switch (buttonType) {
        case 'primary':
          return `${baseClasses} text-[16px] bg-primary-g300 text-white hover:bg-primary-g400 active:bg-primary-g500`;
        case 'secondary':
          return `${baseClasses} text-[16px] leading-[24px] bg-transparent text-text-primary hover:bg-secundary-b50 hover:text-secundary-b400 active:bg-secundary-b100 active:text-secundary-b500`;
        case 'danger':
          return `${baseClasses} text-[16px] leading-[24px] bg-error text-white hover:bg-red-600 active:bg-red-800`;
        default:
          return `${baseClasses} bg-primary-g300 text-white hover:bg-primary-g400`;
      }
    }
  }
  getTitleClasses(): string {
    if (this.modalVariant === 'legacy') {
      return 'font-poppins font-semibold text-[22px] text-secundary-b300 text-center leading-[24px]';
    } else {
      return 'font-poppins font-semibold text-[22px] leading-[24px] xs:leading-[36px] text-[#1A212B] text-center';
    }
  }
  getMessageClasses(): string {
    return 'font-poppins font-medium text-[14px] text-text-primary text-center leading-5 mt-[4px] mb-5';
  }

  getIconClass(): string {
    const iconClass = this.modalService.iconClass();
    return iconClass.replace(/text-\[[^\]]+\]/g, '').replace(/\s+/g, ' ').trim();
  }

  getIconSize(): number {
    if (this.modalVariant === 'legacy') {
      const iconClass = this.modalService.iconClass();
      const sizeMatch = iconClass.match(/text-\[(\d+)px\]/);
      return sizeMatch ? parseInt(sizeMatch[1]) : 34;
    } else {
      const iconClass = this.modalService.iconClass();
      const sizeMatch = iconClass.match(/text-\[(\d+)px\]/);
      return sizeMatch ? parseInt(sizeMatch[1]) : 24;
    }
  }

  getIconBackgroundColor(): string {
    switch (this.modalService.modalType()) {
      case 'success':
        return '#E1FCDE';
      case 'warning':
        return '#FFE4E6';
      case 'confirmation':
        return '#FFF2D2';
      case 'info':
        return '#FFF2D2';
      default:
        return '#4fd1c5';
    }
  }

  getIconShadowColor(): string {
    const backgroundColor = this.getIconBackgroundColor();
    return this.lightenColor(backgroundColor, 0);
  }

  getCircleSizes() {
    if (this.modalVariant === 'legacy') {
      const iconSize = this.getIconSize();
      return {
        outer: iconSize + 40,
      };
    } else {
      return {
        outer: 48,
      };
    }
  }

  getIconColor(): string {
    switch (this.modalService.modalType()) {
      case 'success':
        return 'text-success';
      case 'warning':
        return 'text-rose-600';
      case 'confirmation':
        return 'text-warning';
      default:
        return 'text-primary-g300';
    }
  }

  private lightenColor(color: string, amount: number): string {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    const newR = Math.round(r + (255 - r) * amount);
    const newG = Math.round(g + (255 - g) * amount);
    const newB = Math.round(b + (255 - b) * amount);

    return `rgb(${newR}, ${newG}, ${newB})`;
  }

  onConfirmMiddle() {
    if (this.modalService.onConfirmMiddle) {
      this.modalService.onConfirmMiddle();
    }
    this.modalService.closeModal();
  }
}
